import {
  FaBatteryFull,
  FaBolt,
  FaBuilding,
  FaChargingStation,
  FaSmog,
  FaSolarPanel,
} from "react-icons/fa";
import { IconType } from "react-icons";

import { SurveyTemplateEnum } from "../services/zeos-api/enums/Survey";

/**
 * Optional Property Surveys hub card catalog.
 * Each card maps to an explore SurveyTemplate subset (mode=onboarding).
 */

export type OnboardingSurveyCardId =
  | "power-generation"
  | "building-performance"
  | "carbon-emissions"
  | "battery-storage"
  | "demand-response"
  | "ev-charging";

export type OnboardingSurveyCardKind = "onboarding" | "explore";

export interface IOnboardingSurveyCard {
  id: OnboardingSurveyCardId;
  title: string;
  blurb: string;
  estimatedMinutes: number;
  icon: IconType;
  enabled: boolean;
  kind: OnboardingSurveyCardKind;
  templateId: SurveyTemplateEnum;
  /** Absolute or app path to open when this survey is started */
  route: string;
  pageTitle: string;
  pageMessage: string;
  /** Completion screen after the four onboarding questions */
  completionHeading: string;
  completionBody: string;
  /** Short labels for summary rows, ordered by OnboardingSortOrder */
  summaryLabels: string[];
}

const surveyRoute = (id: OnboardingSurveyCardId): string =>
  `/onboarding/survey/${id}`;

export const OnboardingSurveyCardsConfig: IOnboardingSurveyCard[] = [
  {
    id: "power-generation",
    title: "Onsite Power Generation",
    blurb:
      "Solar and other onsite generation — existing systems or site potential.",
    estimatedMinutes: 2,
    icon: FaSolarPanel,
    enabled: true,
    kind: "onboarding",
    templateId: SurveyTemplateEnum.PowerGen,
    route: surveyRoute("power-generation"),
    pageTitle: "Power Generation",
    pageMessage:
      "ZEOS-AI can help determine whether on-site solar, Community Solar, or another renewable-power option may fit this property and your goals. Answer a few quick questions to personalize your experience. You can skip this now and return at any time.",
    completionHeading: "Your Power Generation preferences are saved.",
    completionBody:
      "ZEOS-AI will use these priorities to personalize the Power Generation experience for this property.",
    summaryLabels: ["Pathway", "Primary goal", "Timing", "Incentives"],
  },
  {
    id: "building-performance",
    title: "Building Performance",
    blurb:
      "Envelope, HVAC, and operations — how your building uses energy today.",
    estimatedMinutes: 5,
    icon: FaBuilding,
    enabled: true,
    kind: "onboarding",
    templateId: SurveyTemplateEnum.BuildingPerformance,
    route: surveyRoute("building-performance"),
    pageTitle: "Building Performance",
    pageMessage:
      "ZEOS-AI can help you understand how your building is using energy, identify unusual changes, and uncover opportunities to improve performance. Answer a few quick questions to personalize your experience. You can skip this now and return at any time.",
    completionHeading: "Your Building Performance preferences are saved.",
    completionBody:
      "ZEOS-AI will use these priorities to personalize the Building Performance experience for this property.",
    summaryLabels: ["Primary goals", "Timing", "Initial focus", "Start with"],
  },
  {
    id: "carbon-emissions",
    title: "Carbon Emissions",
    blurb:
      "Fuel mix and emissions profile — key for compliance and decarbonization.",
    estimatedMinutes: 4,
    icon: FaSmog,
    enabled: true,
    kind: "onboarding",
    templateId: SurveyTemplateEnum.CarbonEmissions,
    route: surveyRoute("carbon-emissions"),
    pageTitle: "Carbon Emissions",
    pageMessage:
      "ZEOS-AI can help establish a carbon profile and prioritize reduction pathways for this property. Answer a few quick questions to personalize your experience. You can skip this now and return at any time.",
    completionHeading: "Your Carbon Emissions preferences are saved.",
    completionBody:
      "ZEOS-AI will use these priorities to personalize the Carbon Emissions experience for this property.",
    summaryLabels: ["Purpose", "Boundary", "Outcomes", "Start with"],
  },
  {
    id: "battery-storage",
    title: "Battery Energy Storage",
    blurb:
      "Existing or planned storage — resilience, peak shaving, and grid revenue.",
    estimatedMinutes: 3,
    icon: FaBatteryFull,
    enabled: true,
    kind: "onboarding",
    templateId: SurveyTemplateEnum.BatteryStorage,
    route: surveyRoute("battery-storage"),
    pageTitle: "Battery Storage",
    pageMessage:
      "Battery storage can help reduce peak costs, support backup power, make better use of solar, and prepare a property for future grid programs. Answer a few quick questions to personalize your experience. You can skip this now and return at any time.",
    completionHeading: "Your Battery Storage preferences are saved.",
    completionBody:
      "ZEOS-AI will use these priorities to personalize the Battery Storage experience for this property.",
    summaryLabels: [
      "Storage situation",
      "Primary goals",
      "Timing",
      "Start with",
    ],
  },
  {
    id: "demand-response",
    title: "Demand Response",
    blurb:
      "Load flexibility and curtailment readiness — earn revenue from the grid.",
    estimatedMinutes: 3,
    icon: FaBolt,
    enabled: true,
    kind: "onboarding",
    templateId: SurveyTemplateEnum.DemandResponse,
    route: surveyRoute("demand-response"),
    pageTitle: "Demand Response",
    pageMessage:
      "ZEOS-AI can help evaluate Demand Response readiness and preferred outcomes for this property. Answer a few quick questions to personalize your experience. You can skip this now and return at any time.",
    completionHeading: "Your Demand Response preferences are saved.",
    completionBody:
      "ZEOS-AI will use these priorities to personalize the Demand Response experience for this property.",
    summaryLabels: ["Situation", "Outcomes", "Timing", "Start with"],
  },
  {
    id: "ev-charging",
    title: "EV Charging",
    blurb:
      "EV charging interest and site readiness for current or future stations.",
    estimatedMinutes: 4,
    icon: FaChargingStation,
    enabled: true,
    kind: "onboarding",
    templateId: SurveyTemplateEnum.EvCharging,
    route: surveyRoute("ev-charging"),
    pageTitle: "EV Charging",
    pageMessage:
      "ZEOS-AI can help evaluate new charging opportunities, organize existing charging assets, and identify ways to manage charging cost and performance. Answer a few quick questions to personalize your experience. You can skip this now and return at any time.",
    completionHeading: "Your EV Charging preferences are saved.",
    completionBody:
      "ZEOS-AI will use these preferences to personalize the EV Charging experience and recommend the appropriate next step.",
    summaryLabels: [
      "Primary motivations",
      "Timing",
      "Intended users",
      "Situation",
    ],
  },
];

export const getOnboardingSurveyCard = (
  id: OnboardingSurveyCardId
): IOnboardingSurveyCard | undefined =>
  OnboardingSurveyCardsConfig.find((card) => card.id === id);

export const getOnboardingSurveyCardBySlug = (
  slug: string
): IOnboardingSurveyCard | undefined =>
  OnboardingSurveyCardsConfig.find((card) => card.id === slug);

export const buildEnabledSurveyQueue = (
  selectedIds: OnboardingSurveyCardId[]
): OnboardingSurveyCardId[] => {
  const enabled = selectedIds.filter((id) => {
    const card = getOnboardingSurveyCard(id);
    return card?.enabled === true;
  });

  // Run onboarding-shell surveys before Explore soft-handoffs.
  return [...enabled].sort((a, b) => {
    const kindA = getOnboardingSurveyCard(a)?.kind === "explore" ? 1 : 0;
    const kindB = getOnboardingSurveyCard(b)?.kind === "explore" ? 1 : 0;
    return kindA - kindB;
  });
};
